import React, { FunctionComponent, useMemo, useCallback, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
  MdOutlineEmail,
  MdOutlinePhone,
} from "react-icons/md";
import {
  Stack,
  Switch,
  Text,
  IconButton,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  SmallCloseIcon,
} from "@chakra-ui/icons";

import { filter, includes } from "lodash";
import { isValidPhoneNumber } from "libphonenumber-js";
import isEmail from "validator/lib/isEmail";

import {
  SiteAlarmRules_siteAlarmRules_rules,
  SiteAlarmRuleActions_siteAlarmRuleActions_actions,
} from "graphql/generated";


interface IProps {
  rule: SiteAlarmRules_siteAlarmRules_rules;
  actions: SiteAlarmRuleActions_siteAlarmRuleActions_actions[] | undefined;
  onChange: (rule: SiteAlarmRules_siteAlarmRules_rules) => void;
  onRemove: () => void;
}

interface ILabeledActions {
  label: string;
  actions: SiteAlarmRuleActions_siteAlarmRuleActions_actions[];
}


const AlarmRule: FunctionComponent<IProps> = ({
  rule,
  actions,
  onChange,
  onRemove,
}) => {
  const intl = useIntl();

  const actionsMapping = useMemo(() => [
    {
      label: intl.formatMessage({
        id: "alarms__alarm_rules__action_verb__show",
        defaultMessage: "Show",
      }),
      types: ["NOTIFICATION"],
    },
    {
      label: intl.formatMessage({
        id: "alarms__alarm_rules__action_verb__send",
        defaultMessage: "Send",
      }),
      types: ["EMAIL", "SMS"],
    },
  ], [intl]);

  const labeledActions = useMemo<ILabeledActions[]>(() => actionsMapping.map(({ label, types }) => ({
    label,
    actions: filter(actions, _ => includes(types, _.type)),
  })), [actionsMapping, actions]);

  const toggleRuleEnabled = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...rule,
      enabled: e.target.checked,
    });
  }, [rule, onChange]);

  const changeRuleAction = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const action = actions?.find(_ => _.id === e.target.value);

    if (!action) return;

    onChange({
      ...rule,
      action: {
        ...rule.action,
        id: action.id,
        type: action.type,
      },
    });
  }, [rule, actions, onChange]);

  const changeRuleConfig = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const action = actions?.find(_ => _.id === rule.action.id);

    if (!action) return;

    const inputValue = e.target.value;

    switch(action.type) {
      case "EMAIL":
        if (isEmail(inputValue)) onChange({
          ...rule,
          config: {
            ...rule.config,
            email: inputValue,
          }
        });
        break;
      case "SMS":
        if (isValidPhoneNumber(inputValue)) onChange({
          ...rule,
          config: {
            ...rule.config,
            phone: inputValue,
          }
        });
        break;
    }
  }, [rule, actions, onChange]);

  const actionLabel = useCallback((action_type: string): string => {
    switch(action_type) {
      case "NOTIFICATION": return intl.formatMessage({id: "alarms__alarm_rules__action_label__notification", defaultMessage: "notification"});
      case "EMAIL": return intl.formatMessage({id: "alarms__alarm_rules__action_label__email", defaultMessage: "e-mail"});
      case "SMS": return intl.formatMessage({id: "alarms__alarm_rules__action_label__sms", defaultMessage: "SMS"});
      default: return action_type;
    }
  }, [intl]);

  // const actionDetails = useMemo(() => <>
  //   <IconButton
  //     icon={<MdMoreHoriz/>}
  //     aria-label={intl.formatMessage({
  //       id: "alarms__alarm_rules__rule_action_details",
  //       defaultMessage: "Action details",
  //     })}
  //     size="xs"
  //   />
  // </>, [intl]);

  const [localEmail, setLocalEmail] = useState(rule.config.email || "");
  const [localPhone, setLocalPhone] = useState(rule.config.phone || "");

  useEffect(() => { setLocalEmail(rule.config.email || ""); }, [rule.config.email]);
  useEffect(() => { setLocalPhone(rule.config.phone || ""); }, [rule.config.phone]);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const prevActionType = useRef(rule.action.type);

  useEffect(() => {
    if (rule.action.type === prevActionType.current) return;

    prevActionType.current = rule.action.type;

    if (rule.action.type === "EMAIL") emailInputRef.current?.focus();
    else if (rule.action.type === "SMS") phoneInputRef.current?.focus();
  }, [rule.action.type]);

  const isEmailInvalid = !localEmail || !isEmail(localEmail);
  const isPhoneInvalid = !localPhone || !isValidPhoneNumber(localPhone);

  return (
    <>

      <Stack direction="row" spacing={3} alignItems="baseline" my={1}>
        <Switch
          isChecked={rule.enabled}
          size="sm"
          onChange={toggleRuleEnabled}
          title={
            rule.enabled
            ? intl.formatMessage({
              id: "alarms__alarm_rules__rule_disable",
              defaultMessage: "Disable rule",
            })
            : intl.formatMessage({
              id: "alarms__alarm_rules__rule_enable",
              defaultMessage: "Enable rule",
            })
          }
        />
        <Stack direction="row" flexGrow={1} alignItems="baseline" justifyContent="flex-start" flexWrap="wrap">
          <Text fontSize="sm">{labeledActions.find(({ actions }) => actions.some(a => a.id === rule.action.id))?.label}</Text>
          <Text fontSize="sm" position="relative" textDecoration="underline">
            {actionLabel(rule.action.type)}
            <Select
              value={rule.action.id}
              position="absolute"
              left={0}
              right={0}
              top={0}
              bottom={0}
              size="sm"
              width="auto"
              color="transparent"
              onChange={changeRuleAction}
              variant="unstyled"
              cursor="pointer"
            >
              {labeledActions.map(({label, actions}, index) => <>
                <option disabled key={index} style={{background: "initial"}} color="gray.500">{label}:</option>
                {actions.map((action) => <option value={action.id} key={action.id} style={{background: "initial", color: "initial"}}>- {actionLabel(action.type)}</option>)}
              </>)}
            </Select>
          </Text>
          {
            rule.action.type === "NOTIFICATION"
            && <>
              {/* {actionDetails}
              <Text fontSize="sm" whiteSpace="nowrap">saying</Text>
              <Input
                value={rule.config.message || ""}
                size="sm"
                width="auto"
                placeholder="Optional"
              /> */}
            </>
          }
          {
            rule.action.type === "EMAIL"
            && <>
              <Text fontSize="sm" whiteSpace="nowrap">
                {intl.formatMessage({
                  id: "alarms__alarm_rules__action_to",
                  defaultMessage: "to",
                })}
              </Text>
              <InputGroup
                size="xs"
                width="auto"
              >
                <InputLeftElement
                  children={<MdOutlineEmail />}
                  pointerEvents="none"
                />
                <Input
                  value={localEmail}
                  type="email"
                  size="xs"
                  ref={emailInputRef}
                  errorBorderColor="red.500"
                  focusBorderColor={isEmailInvalid ? "red.500" : undefined}
                  isInvalid={isEmailInvalid}
                  onChange={e => setLocalEmail(e.target.value)}
                  onBlur={changeRuleConfig}
                  placeholder={intl.formatMessage({
                    id: "alarms__alarm_rules__config__email",
                    defaultMessage: "E-mail address",
                  })}
                />
              </InputGroup>
              {/* {actionDetails}
              <Text fontSize="sm" whiteSpace="nowrap">with subject</Text>
              <Input
                value={rule.config.subject || ""}
                size="sm"
                width="auto"
                placeholder="Optional"
              /> */}
            </>
          }
          {
            rule.action.type === "SMS"
            && <>
              <Text fontSize="sm" whiteSpace="nowrap">
                {intl.formatMessage({
                  id: "alarms__alarm_rules__action_to",
                  defaultMessage: "to",
                })}
              </Text>
              <InputGroup
                size="xs"
                width="auto"
              >
                <InputLeftElement
                  children={<MdOutlinePhone />}
                  pointerEvents="none"
                />
                <Input
                  value={localPhone}
                  type="tel"
                  size="xs"
                  width="auto"
                  ref={phoneInputRef}
                  errorBorderColor="red.500"
                  focusBorderColor={isPhoneInvalid ? "red.500" : undefined}
                  isInvalid={isPhoneInvalid}
                  onChange={e => setLocalPhone(e.target.value)}
                  onBlur={changeRuleConfig}
                  placeholder={intl.formatMessage({
                    id: "alarms__alarm_rules__config__phone",
                    defaultMessage: "Phone number (+XXXXX...)",
                  })}
                />
              </InputGroup>
              {/* {actionDetails} */}
            </>
          }
        </Stack>
        <IconButton
          icon={<SmallCloseIcon/>}
          aria-label="Remove button"
          title={intl.formatMessage({
            id: "alarms__alarm_rules__rule_remove",
            defaultMessage: "Remove rule",
          })}
          size="xs"
          onClick={onRemove}
        />
        {/* <DragHandleIcon
        /> */}
      </Stack>

    </>
  );
};

export default AlarmRule;
